/**
 * slash-app のマンスリーレッスン「シフト調整」API ヘルパ。
 *
 * 講師が出したシフト（出られる日）の表示、回の登録、講師への確定通知、
 * 提出内容の修正は slash-app 側に集約されている（祝日・重なりの判定や
 * プッシュ通知もそこで行う）。ここは bridge JWT で slash-app の
 * IsAdmin エンドポイントを叩くだけ。
 */
import { slashAppJson } from "./slashAppClient";

const BASE = "/api/lessons/monthly/admin";

export const slashAppMonthly = {
  /** 未確定のシフトがあるか。{ has_unconfirmed } */
  summary() {
    return slashAppJson(`${BASE}/summary/`);
  },

  /**
   * シフト調整の表。month を省くと未確定のある一番早い月（無ければ受付中の月）。
   * @param {string} [month] "YYYY-MM"
   */
  shiftBoard(month) {
    const q = month ? `?month=${encodeURIComponent(month)}` : "";
    return slashAppJson(`${BASE}/shift-board/${q}`);
  },

  /** その日に講師本人とスタジオが埋まっている時間帯。{ date, slots } */
  daySchedule(instructorId, studioId, date) {
    const q = new URLSearchParams({ instructor_id: instructorId, date });
    if (studioId) q.set("studio_id", studioId);
    return slashAppJson(`${BASE}/day-schedule/?${q.toString()}`);
  },

  /**
   * 回をまとめて登録する（承認済みで作るので、すぐホームページに掲載される）。
   * @param {{instructor_id:number, date:string, lessons:Array<{source_lesson_id:number,
   *   start_time:string, capacity:number, price:number, member_price:(number|null)}>}} body
   */
  registerLessons(body) {
    return slashAppJson(`${BASE}/lessons/`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /** 回の作成・編集で選べる店舗と講師。{ studios, instructors } */
  lessonOptions() {
    return slashAppJson(`${BASE}/lesson-options/`);
  },

  /**
   * シフトと関係なく回を1件作る（新しいクラスも可）。承認済みで作るのですぐ掲載される。
   * @param {{name:string, studio_id:number, instructor_id:number, date:string,
   *   start_time:string, end_time:string, capacity:number, price:number,
   *   member_price:(number|null)}} body
   */
  createLesson(body) {
    return slashAppJson(`${BASE}/lessons/new/`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  /** 回の内容を書き換える（body は createLesson と同じ）。申込者・講師への連絡はしない。 */
  updateLesson(lessonId, body) {
    return slashAppJson(`${BASE}/lessons/${lessonId}/`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  /** 講師ごとに日程の確定（回が無ければ開催なし）を通知する。{ notified, failed } */
  confirm(month, instructorIds) {
    return slashAppJson(`${BASE}/confirm/`, {
      method: "POST",
      body: JSON.stringify({ month, instructor_ids: instructorIds }),
    });
  },

  /** 提出内容の修正・代理提出（講師へは通知しない）。 */
  saveSubmission(instructorId, month, { note, days }) {
    return slashAppJson(`${BASE}/submissions/${instructorId}/${month}/`, {
      method: "PUT",
      body: JSON.stringify({ note, days }),
    });
  },

  /** 提出内容の削除。 */
  deleteSubmission(instructorId, month) {
    return slashAppJson(`${BASE}/submissions/${instructorId}/${month}/`, {
      method: "DELETE",
    });
  },
};

/** slashAppJson の "HTTP 400: 理由" から理由だけ取り出す。 */
export function slashAppErrorMessage(e, fallback) {
  if (e && e.status === 401) {
    return "slash-app へのログインが切れています。再度ログインしてください。";
  }
  const detail = String((e && e.message) || "").replace(/^HTTP \d+:\s*/, "");
  // 未デプロイなどで HTML のエラーページが返ったときは中身を出さない
  if (!detail || detail.trimStart().startsWith("<")) return fallback;
  return detail;
}
